import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

type JsonDisplayLine = {
  key: string;
  value: string;
  depth: number;
};

@Component({
  selector: 'procure-pretty-json',
  templateUrl: './pretty-json.component.html',
  styleUrl: './pretty-json.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrettyJson {
  readonly value = input<unknown>(null);
  readonly indent = input(16);

  protected readonly lines = computed<JsonDisplayLine[]>(() => {
    const value = this.value();

    if (value === null || value === undefined) {
      return [];
    }

    if (typeof value !== 'object') {
      return [
        {
          key: 'value',
          value: this.stringifyPrimitive(value),
          depth: 0,
        },
      ];
    }

    return this.buildJsonLines(value as Record<string, unknown> | unknown[]);
  });

  private buildJsonLines(
    value: Record<string, unknown> | unknown[],
    depth = 0,
    parentKey?: string,
  ): JsonDisplayLine[] {
    const isArray = Array.isArray(value);
    const lines: JsonDisplayLine[] = [];

    if (parentKey !== undefined) {
      lines.push({
        key: parentKey,
        value: '',
        depth,
      });
    }

    const entries = isArray
      ? (value as unknown[]).map((item, index) => [`[${index}]`, item] as const)
      : Object.entries(value);

    if (!entries.length && parentKey === undefined) {
      return [
        {
          key: isArray ? 'Array' : 'Object',
          value: '—',
          depth,
        },
      ];
    }

    const nextDepth = parentKey === undefined ? depth : depth + 1;

    for (const [childKey, childValue] of entries) {
      if (childValue !== null && typeof childValue === 'object') {
        lines.push(
          ...this.buildJsonLines(
            childValue as Record<string, unknown> | unknown[],
            nextDepth,
            childKey,
          ),
        );
      } else {
        lines.push({
          key: childKey,
          value: this.stringifyPrimitive(childValue),
          depth: nextDepth,
        });
      }
    }

    return lines;
  }

  private stringifyPrimitive(value: unknown): string {
    if (value === null || value === undefined) {
      return '—';
    }

    if (typeof value === 'string') {
      return value;
    }

    return String(value);
  }
}
